<script setup lang="ts">
import { memberEmail, memberMailDomain } from '@zerohuman/shared'
import {
    Checkbox,
    Button as KitButton,
    Input as KitInput,
    Select as KitSelect,
    Textarea as KitTextarea,
    Label,
    RadioGroup,
    RadioGroupItem,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    TagsInput,
    TagsInputItem,
    TagsInputItemDelete,
    TagsInputItemText,
    ZhEmptyState,
    ZhFormField,
    ZhFormLayout,
    ZhSavePreview,
    ZhSectionHeading,
} from '@zerohuman/ui'
import { computed, onMounted, ref, watch } from 'vue'
import { type RouteLocationRaw, RouterLink, useRouter } from 'vue-router'
import {
    type AgentConnection,
    api,
    asListResponse,
    type CatalogCloneReview,
    type CatalogReviewSlot,
    type CatalogRole,
    type CatalogSlotFill,
    type CatalogTask,
    type CatalogToolSwap,
    CRAFTED_NOT_IN_PLAN_MESSAGE,
    isCraftedRefusal,
    type Me,
    type Role,
    requestFailure,
    type Team,
} from '@/api'
import MemberAvatar from '@/components/MemberAvatar.vue'
import { useUiMode } from '@/composables/useUiMode'
import { requireAiAccount } from '@/lib/aiAccountGuard'
import { avatarDataUrl } from '@/lib/avatar'
import { clonableTasks, roleLine, taskDisplayName } from '@/lib/catalogText'
import { buildTeammatePreview } from '@/lib/formPreview'
import { pickedId } from '@/lib/memberPickers'
import { monthlyCostLines } from '@/lib/monthlyCost'
import { toolLabel } from '@/lib/presentation'
import { heldCopyOf, refusalWords, roleNamed, slugFromName, type ToolNeed, toolNeeds } from '@/lib/teammateAdd'
import {
    clearTeammateDraft,
    type DraftJob,
    keepTeammateDraft,
    type TeammateFormDraft,
    takeTeammateDraft,
} from '@/lib/teammateDraft'
import { type HandedFirstTask, keepTeammateHandoff, keepTeammateNotice } from '@/lib/teammateHandoff'
import { connectToolLocation } from '@/lib/teamSetup'

/**
 * "Add a teammate" (#1758): one screen in place of the separate Members, Roles, Agents and Tools steps. The person
 * chooses a job (a Catalog role, or one they describe), names the teammate and their personality, picks the team and
 * the AI account, sees each tool the job needs with why and a Connect button, and gives the first work. The role is
 * created or reused from the job, never chosen in a step of its own.
 *
 * - A Catalog job is one role-clone call with create-teammate, as the Catalog's Clone Role panel makes. When the
 *   company already holds that job on the chosen team, the screen says so before anything is sent and offers no tasks
 *   it would not install; the teammate is made with Create member's calls and given that role (#2128).
 * - A described job reuses the company's role of exactly that name, or creates one, then makes the member with Create
 *   member's calls. Its first task is finished on Create task, prefilled.
 * - The company's AI account is the default; "its own account" lands on the Agents page to connect it there.
 * - Every step after the teammate exists is tried; one that fails is named once on the teammate's page (#2128).
 * - Connect and "Create a team first" keep the form for this tab, and it comes back on return (#2128).
 *
 * Everything is the kit's, and the screen states no theme value. The kit's field, picker, text box and button are
 * imported under Kit-prefixed names so the template never spells a tag that reads as a native control of its own.
 */
const router = useRouter()
const { mode } = useUiMode()

/** The daily cap while the enterprise's Day $ is unknown, and its bounds (USD): the clone panel's. */
const FALLBACK_DAILY_CAP_USD = 10
const MIN_DAILY_CAP_USD = 0.01
const MAX_DAILY_CAP_USD = 10000
/** A dollar amount as the cap's help says it: $10,000, $0.01. */
const dollars = (amount: number) => `$${amount.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
/** The longest member name the API takes; a job's title copied into the name is cut to it. */
const NAME_MAX = 80
/** How many Catalog jobs one search shows. */
const JOB_PAGE = 12
/** What a picker that only adds holds: nothing, so it always reads its placeholder. */
const NOTHING = ''
/** The tool choices a kept task has for a tool the company has not connected: the clone panel's. */
const KEEP = 'keep'
const DROP = 'drop'
const USE = 'use:'

const teams = ref<Team[]>([])
const roles = ref<Role[]>([])
const me = ref<Me | null>(null)

/** The job: a Catalog role, or one the person describes. */
type Job = DraftJob
const job = ref<Job | null>(null)
const jobs = ref<CatalogRole[]>([])
const jobSearch = ref('')
const jobsLoading = ref(true)
const jobsFailed = ref(false)
const jobTitle = ref('')
const jobLine = ref('')
const keptTaskIds = ref<string[]>([])
const catalogJob = computed(() => (job.value?.kind === 'catalog' ? job.value.role : null))
const described = computed(() => job.value?.kind === 'described')
/** The tasks a Catalog job can give: never OS-origin or deprecated ones, which no clone takes. */
const jobTasks = computed(() => clonableTasks(catalogJob.value?.tasks))
/** The job as the screen names it: the Catalog role's name, or the title typed. */
const jobName = computed(() => catalogJob.value?.name ?? jobTitle.value.trim())

/** The name is the job's title until the person names the teammate; the persona is the job's line until typed. */
const name = ref('')
const nameTyped = ref(false)
const persona = ref('')
const personaTyped = ref(false)
const teamId = ref('')
const aiProvider = ref<'shared' | 'own'>('shared')
const firstTask = ref('')

/** Advanced: the email follows the name until typed, as on Create member. */
const emailTyped = ref('')
const emailEdited = ref(false)
const avatar = ref<string | null>(null)
const avatarInput = ref<InstanceType<typeof KitInput> | null>(null)
const money = ref<number | string>(FALLBACK_DAILY_CAP_USD)
/** Set once the cap is edited: the enterprise's Day $ arriving later never overwrites what was typed. */
const moneyTyped = ref(false)
/**
 * Where the cap's starting value came from (#2128): the company's Day $ once read; the nearest allowed cap when that
 * Day $ is above or below what a teammate may have ('above', 'below'); or the $10 fallback while it is being read and
 * when there is none or the read failed. The help says which, and a teammate made with Create member's calls gets any
 * value but the company's own as their own cap, so they get exactly the cap shown.
 */
const capSource = ref<'reading' | 'company' | 'above' | 'below' | 'fallback'>('reading')
const slugTyped = ref('')
const slugEdited = ref(false)
const extraTeamIds = ref<string[]>([])
const extraRoleIds = ref<string[]>([])

/**
 * The company's AI-account connections, for the expected monthly cost (#1723): undefined while the read is out, null
 * when it failed. The estimate follows the AI account choice and the daily cap as they change.
 */
const connections = ref<AgentConnection[] | null | undefined>(undefined)
const monthlyCost = computed(() =>
    connections.value === undefined
        ? []
        : monthlyCostLines({
              connections: connections.value,
              aiAccount: aiProvider.value,
              teammates: 1,
              dailyCapUsd: money.value,
          }),
)

/** The role review: per task, the tools the company has not connected and the connected ones that could stand in. */
const review = ref<CatalogCloneReview['tasks']>([])
const reviewState = ref<'loading' | 'ready' | 'failed'>('loading')
/** What the person chose per kept task and missing tool. A tool with no entry is kept, to connect later. */
const swapChoices = ref<Record<string, string>>({})
/** The tool slots the job's tasks declare, each with the connection filling it and the ones that could. */
const reviewSlots = ref<CatalogReviewSlot[]>([])
/** The connection chosen per slot key. A slot with no entry keeps the one already filling it, or stays unfilled. */
const slotChoices = ref<Record<string, string>>({})
/** The Select's value for leaving a slot unfilled: the kit Select refuses an empty value, and binding ids are UUIDs. */
const NO_FILL = ':no-fill'

const busy = ref(false)
const error = ref<string | null>(null)
/** Which plan refusal the last send met, if any: each has its own sentence and its own link to Billing. */
const upgrade = ref<'members' | 'crafted' | null>(null)

/**
 * The company's own copy of the chosen Catalog job (#2128): the role with its slug, else its name. A role is one job
 * across the company, so a teammate given it holds it on the chosen team and takes the tasks that team already has for
 * it; the screen offers no ticks, swaps or tools for it and installs nothing.
 */
const heldJob = computed(() => (catalogJob.value ? heldCopyOf(roles.value, catalogJob.value) : undefined))
/** Set when a clone refused as already cloned found the held copy only on reading the roles again: nothing was sent. */
const heldOnSend = ref(false)
/** The held job's notice: the teammate holds the job the company has, and takes the chosen team's tasks for it. */
const heldNotice = computed(() => {
    if (!heldJob.value) return ''
    const where = teamName(teamId.value) || 'this team'
    return `Your company already has this job, so your teammate holds it and takes the tasks ${where} has for it. No tasks are added; to give ${where} its own copies of the job's tasks, clone the job onto it from the Catalog.`
})

const needs = computed(() => (heldJob.value ? [] : toolNeeds(jobTasks.value, keptTaskIds.value, review.value)))
const mailDomain = computed(() => (me.value ? memberMailDomain(me.value.enterprise.slug) : ''))
const derivedEmail = computed(() =>
    mailDomain.value && name.value.trim() ? memberEmail(name.value, mailDomain.value) : '',
)
const emailValue = computed(() => (emailEdited.value ? emailTyped.value : derivedEmail.value))
const slugValue = computed(() => (slugEdited.value ? slugTyped.value : slugFromName(jobTitle.value)))
const preview = computed(() => ({ name: name.value, avatarUrl: avatar.value }))
/** What the collapsed Advanced section holds, and so the count its header shows. */
const advancedFields = computed(() => [
    'email',
    'photo',
    'teams',
    'roles',
    'daily cap',
    ...(described.value ? ['short name'] : []),
])
const extraTeamChoices = computed(() =>
    teams.value.filter(team => team.id !== teamId.value && !extraTeamIds.value.includes(team.id)),
)
const extraRoleChoices = computed(() => roles.value.filter(role => !extraRoleIds.value.includes(role.id)))

/** The cap's help line: what it is, and where its starting value came from (#2128). */
const capHelp = computed(() => {
    const what = 'The most they may spend in a day.'
    if (capSource.value === 'company') return `${what} Starts at your company's daily cap.`
    if (capSource.value === 'above')
        return `${what} Starts at ${dollars(MAX_DAILY_CAP_USD)}, the most allowed, as your company's daily cap is higher.`
    if (capSource.value === 'below')
        return `${what} Starts at ${dollars(MIN_DAILY_CAP_USD)}, the least allowed, as your company's daily cap is lower.`
    const fallback = dollars(FALLBACK_DAILY_CAP_USD)
    if (capSource.value === 'fallback')
        return `${what} Starts at ${fallback}, as your company's daily cap could not be found.`
    return `${what} Starts at ${fallback} until your company's daily cap is read.`
})

/** The form as it stands, kept for this tab before Connect or "Create a team first" leaves it (#2128). */
function keepDraft(): void {
    keepTeammateDraft({
        job: job.value,
        jobSearch: jobSearch.value,
        jobTitle: jobTitle.value,
        jobLine: jobLine.value,
        keptTaskIds: [...keptTaskIds.value],
        swapChoices: { ...swapChoices.value },
        slotChoices: { ...slotChoices.value },
        name: name.value,
        nameTyped: nameTyped.value,
        persona: persona.value,
        personaTyped: personaTyped.value,
        teamId: teamId.value,
        aiProvider: aiProvider.value,
        firstTask: firstTask.value,
        emailTyped: emailTyped.value,
        emailEdited: emailEdited.value,
        avatar: avatar.value,
        money: String(money.value),
        moneyTyped: moneyTyped.value,
        slugTyped: slugTyped.value,
        slugEdited: slugEdited.value,
        extraTeamIds: [...extraTeamIds.value],
        extraRoleIds: [...extraRoleIds.value],
    })
}

/**
 * Puts a kept form back as it was left: typed values stay typed, so a job chosen now does not overwrite them. The cap
 * comes back only when it was typed; otherwise it starts again from the company's Day $. The tool review runs again on
 * mount, so a tool connected meanwhile drops off.
 */
function restoreDraft(kept: TeammateFormDraft): void {
    job.value = kept.job
    jobSearch.value = kept.jobSearch
    jobTitle.value = kept.jobTitle
    jobLine.value = kept.jobLine
    keptTaskIds.value = kept.keptTaskIds
    swapChoices.value = kept.swapChoices
    slotChoices.value = kept.slotChoices
    name.value = kept.name
    nameTyped.value = kept.nameTyped
    persona.value = kept.persona
    personaTyped.value = kept.personaTyped
    teamId.value = kept.teamId
    aiProvider.value = kept.aiProvider
    firstTask.value = kept.firstTask
    emailTyped.value = kept.emailTyped
    emailEdited.value = kept.emailEdited
    avatar.value = kept.avatar
    if (kept.moneyTyped) {
        money.value = kept.money
        moneyTyped.value = true
    }
    slugTyped.value = kept.slugTyped
    slugEdited.value = kept.slugEdited
    extraTeamIds.value = kept.extraTeamIds
    extraRoleIds.value = kept.extraRoleIds
}

// Put back before any watcher is set up, so restoring is not taken for the person changing the form.
const keptDraft = takeTeammateDraft()
if (keptDraft) restoreDraft(keptDraft)

onMounted(async () => {
    void loadJobs()
    void prefillMoney()
    void loadConnections()
    if (catalogJob.value) void loadReview()
    const [allTeams, allRoles, session] = await Promise.all([
        api.teams().catch(() => [] as Team[]),
        api.roles().catch(() => [] as Role[]),
        api.me().catch(() => null),
    ])
    teams.value = allTeams
    roles.value = allRoles
    me.value = session
    // The first team, unless a kept form holds one that still exists: a team made meanwhile is chosen when it held none.
    if (!allTeams.some(team => team.id === teamId.value)) teamId.value = allTeams[0]?.id ?? ''
})

let jobsSequence = 0
/** The Catalog's roles matching the search, as the Catalog lists them. */
async function loadJobs(): Promise<void> {
    const sequence = ++jobsSequence
    jobsLoading.value = true
    jobsFailed.value = false
    const query = { page: 1, limit: JOB_PAGE, search: jobSearch.value.trim(), sort: 'name' }
    try {
        const listed = asListResponse(await api.catalogRoles(query), query)
        if (sequence !== jobsSequence) return
        jobs.value = listed.data
    } catch {
        if (sequence !== jobsSequence) return
        jobs.value = []
        jobsFailed.value = true
    } finally {
        if (sequence === jobsSequence) jobsLoading.value = false
    }
}
watch(jobSearch, () => void loadJobs())

/**
 * The daily cap starts at the enterprise's Day $ (FM4), read from the Spend snapshot as the clone panel reads it, and
 * stays at $10 when there is none or the read fails. A Day $ outside the field's bounds starts at the nearest bound,
 * and is marked so: the help says why the number differs, and the teammate gets that bound as their own cap (#2128).
 */
async function prefillMoney(): Promise<void> {
    let cents: number | null | undefined
    try {
        cents = (await api.spend()).caps.find(cap => cap.layer === 'enterprise')?.moneyCentsDay
    } catch {
        capSource.value = 'fallback'
        return
    }
    if (cents == null) {
        capSource.value = 'fallback'
        return
    }
    const company = cents / 100
    if (company > MAX_DAILY_CAP_USD) capSource.value = 'above'
    else if (company < MIN_DAILY_CAP_USD) capSource.value = 'below'
    else capSource.value = 'company'
    if (moneyTyped.value) return
    money.value = Math.min(MAX_DAILY_CAP_USD, Math.max(MIN_DAILY_CAP_USD, company))
}

/** The company's connections for the estimate. A failed read leaves only the cap line, never an error or a guess. */
async function loadConnections(): Promise<void> {
    try {
        const answer = await api.agentConnections()
        connections.value = Array.isArray(answer?.connections) ? answer.connections : null
    } catch {
        connections.value = null
    }
}

function forgetRefusal(): void {
    error.value = null
    upgrade.value = null
    heldOnSend.value = false
}

/** A Catalog job: its name and line fill the teammate's until typed, and every task it can give starts ticked. */
function chooseJob(role: CatalogRole): void {
    job.value = { kind: 'catalog', role }
    keptTaskIds.value = clonableTasks(role.tasks).map(task => task.id)
    swapChoices.value = {}
    slotChoices.value = {}
    if (!nameTyped.value) name.value = role.name.slice(0, NAME_MAX)
    if (!personaTyped.value) persona.value = roleLine(role)
    forgetRefusal()
    void loadReview()
}

/** A described job: a typed title and an optional line, which fill the name and persona until they are typed. */
function describeIt(): void {
    job.value = { kind: 'described' }
    keptTaskIds.value = []
    swapChoices.value = {}
    slotChoices.value = {}
    review.value = []
    reviewSlots.value = []
    // A job title may run to 200 characters, but a member's name to 80: the name takes the title cut to fit.
    if (!nameTyped.value) name.value = jobTitle.value.trim().slice(0, NAME_MAX)
    if (!personaTyped.value) persona.value = jobLine.value.trim()
    forgetRefusal()
}
watch(jobTitle, title => {
    if (described.value && !nameTyped.value) name.value = title.trim().slice(0, NAME_MAX)
})
watch(jobLine, line => {
    if (described.value && !personaTyped.value) persona.value = line.trim()
})

let reviewSequence = 0
/** Opens the Catalog job's review on the team chosen now: what each task needs that the company has not connected. */
async function loadReview(): Promise<void> {
    const role = catalogJob.value
    const sequence = ++reviewSequence
    review.value = []
    reviewSlots.value = []
    reviewState.value = 'loading'
    if (!role) return
    try {
        const answer = await api.catalogRoleReview(role.id, teamId.value || undefined)
        if (sequence !== reviewSequence) return
        review.value = Array.isArray(answer?.tasks) ? answer.tasks : []
        reviewSlots.value = Array.isArray(answer?.slots) ? answer.slots : []
        reviewState.value = 'ready'
    } catch {
        if (sequence === reviewSequence) reviewState.value = 'failed'
    }
}
watch(teamId, () => {
    if (catalogJob.value) void loadReview()
})

function typeName(value: string | number): void {
    name.value = String(value)
    nameTyped.value = true
}

function typePersona(value: string): void {
    persona.value = value
    personaTyped.value = true
}

function chooseTeam(choice: unknown): void {
    teamId.value = pickedId(choice)
    extraTeamIds.value = extraTeamIds.value.filter(id => id !== teamId.value)
}

function chooseProvider(choice: unknown): void {
    aiProvider.value = choice === 'own' ? 'own' : 'shared'
}

/** Ticking or unticking a task starts its tool choices over. */
function tickTask(taskId: string, checked: boolean | 'indeterminate'): void {
    keptTaskIds.value =
        checked === true
            ? jobTasks.value
                  .filter(task => task.id === taskId || keptTaskIds.value.includes(task.id))
                  .map(task => task.id)
            : keptTaskIds.value.filter(id => id !== taskId)
    swapChoices.value = Object.fromEntries(
        Object.entries(swapChoices.value).filter(([key]) => !key.startsWith(`${taskId}\n`)),
    )
}

const swapKey = (taskId: string, tool: string) => `${taskId}\n${tool}`
const candidatesOf = (taskId: string) => review.value.find(row => row.taskId === taskId)?.candidates ?? []
/** The kept tasks behind a tool row that a connected tool could stand in for. */
const swappable = (need: ToolNeed) => need.tasks.filter(task => candidatesOf(task.id).length > 0)

/** A kept task's choice for a missing tool: a connected tool to use instead, or Keep when none is chosen or stands. */
function choiceOf(taskId: string, tool: string): string {
    const chosen = swapChoices.value[swapKey(taskId, tool)]
    return chosen?.startsWith(USE) && candidatesOf(taskId).includes(chosen.slice(USE.length)) ? chosen : KEEP
}

/** Drop is the same as unticking the task; any other choice is kept for the send. */
function setChoice(task: CatalogTask, tool: string, value: unknown): void {
    const choice = pickedId(value) || KEEP
    if (choice === DROP) {
        tickTask(task.id, false)
        return
    }
    swapChoices.value = { ...swapChoices.value, [swapKey(task.id, tool)]: choice }
}

/** The chosen swaps of the kept tasks. Keep sends none, so a send with none is the clone's usual request. */
function toolSwaps(): CatalogToolSwap[] {
    return needs.value.flatMap(need =>
        need.tasks.flatMap(task => {
            const choice = choiceOf(task.id, need.tool)
            return choice.startsWith(USE) ? [{ taskId: task.id, from: need.tool, to: choice.slice(USE.length) }] : []
        }),
    )
}

/**
 * The slots the kept tasks declare, each naming only the kept tasks that need it. A held job installs no Catalog task
 * (#2128), so it has no slot to fill and the send carries no fill.
 */
const slotRows = computed(() =>
    heldJob.value
        ? []
        : reviewSlots.value.flatMap(slot => {
              const tasks = jobTasks.value.filter(
                  task => keptTaskIds.value.includes(task.id) && slot.taskIds.includes(task.id),
              )
              return tasks.length ? [{ ...slot, tasks }] : []
          }),
)
/** The connections a slot may be filled with, the one already filling it among them. */
function slotOptions(slot: CatalogReviewSlot) {
    const { filledBy, candidates } = slot
    return filledBy && !candidates.some(option => option.bindingId === filledBy.bindingId)
        ? [filledBy, ...candidates]
        : candidates
}
/** The connection chosen for a slot: the person's pick while it is still offered, else the one already filling it. */
function slotChoice(slot: CatalogReviewSlot): string {
    const chosen = slotChoices.value[slot.key]
    if (chosen && slotOptions(slot).some(option => option.bindingId === chosen)) return chosen
    return slot.filledBy?.bindingId ?? NO_FILL
}
function chooseSlot(slot: CatalogReviewSlot, value: unknown): void {
    slotChoices.value = { ...slotChoices.value, [slot.key]: pickedId(value) || NO_FILL }
}
/** The fills to send: each slot whose chosen connection is not the one already filling it. */
function slotFills(): CatalogSlotFill[] {
    return slotRows.value.flatMap(slot => {
        const bindingId = slotChoice(slot)
        return bindingId !== NO_FILL && bindingId !== slot.filledBy?.bindingId ? [{ slot: slot.key, bindingId }] : []
    })
}

/**
 * Opens the Tools page's connect flow for this tool type, where onboarding's team setup sends its Connect. The form is
 * kept for this tab first, and comes back when the person returns.
 */
async function connect(tool: string): Promise<void> {
    keepDraft()
    await router.push(connectToolLocation(tool))
}

/** Cancel forgets the kept form, so the next visit starts afresh. */
async function cancel(): Promise<void> {
    clearTeammateDraft()
    await router.push('/members')
}

function typeEmail(value: string | number): void {
    emailTyped.value = String(value)
    emailEdited.value = emailTyped.value.length > 0
}

function typeSlug(value: string | number): void {
    slugTyped.value = String(value)
    slugEdited.value = slugTyped.value.length > 0
}

function typeMoney(value: string | number): void {
    moneyTyped.value = true
    money.value = value
}

const moneyCents = () => Math.round(Number(money.value) * 100)

function teamName(id: unknown): string {
    return teams.value.find(team => team.id === id)?.name ?? ''
}

function roleName(id: unknown): string {
    return roles.value.find(role => role.id === id)?.name ?? ''
}

function addExtraTeam(choice: unknown): void {
    const id = pickedId(choice)
    if (id && !extraTeamIds.value.includes(id)) extraTeamIds.value = [...extraTeamIds.value, id]
}

function removeExtraTeam(id: unknown): void {
    extraTeamIds.value = extraTeamIds.value.filter(held => held !== String(id))
}

function addExtraRole(choice: unknown): void {
    const id = pickedId(choice)
    if (id && !extraRoleIds.value.includes(id)) extraRoleIds.value = [...extraRoleIds.value, id]
}

function removeExtraRole(id: unknown): void {
    extraRoleIds.value = extraRoleIds.value.filter(held => held !== String(id))
}

function pickAvatar(): void {
    ;(avatarInput.value?.$el as HTMLInputElement | undefined)?.click()
}

async function onAvatarPicked(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    input.value = ''
    if (!file) return
    try {
        avatar.value = await avatarDataUrl(file)
        error.value = null
    } catch (caught) {
        error.value =
            caught instanceof Error ? `Could not use that photo (${caught.message}).` : 'Could not use that photo.'
    }
}

/** Takes a picked photo back: their initials stand in again, and no photo is sent. */
function removeAvatar(): void {
    avatar.value = null
}

/** What stops the form being sent, in plain words, or null when nothing does. */
function problem(): string | null {
    if (!job.value) return 'Choose a job from the Catalog, or describe it.'
    if (described.value && !jobTitle.value.trim()) return 'Give the job a title.'
    if (!name.value.trim()) return 'Give your teammate a name.'
    if (!teamId.value) return 'Create a team first, then add your teammate to it.'
    if (!(Number(money.value) > 0)) return 'Set a daily spend cap above $0.'
    // A Catalog job must keep a task; a described job's first work is this sentence, so it is needed too.
    if (described.value && !firstTask.value.trim()) return 'Say what they should do first, in one sentence.'
    return null
}

/**
 * The save preview (#1756): pressing Add teammate first checks the form as it always has, then says in plain words who
 * will join which team as what, and nothing is sent until that is confirmed. Any change to the form hides it as stale.
 */
const savePreview = ref<string[] | null>(null)

watch(
    [
        job,
        jobTitle,
        jobLine,
        keptTaskIds,
        name,
        persona,
        teamId,
        aiProvider,
        firstTask,
        emailTyped,
        avatar,
        money,
        slugTyped,
        extraTeamIds,
        extraRoleIds,
        swapChoices,
        slotChoices,
    ],
    () => {
        savePreview.value = null
        heldOnSend.value = false
    },
    { deep: true },
)

/** The daily cap as the preview says it, or null when none above $0 is set. */
function dailyCapUsd(): number | null {
    const usd = Number(money.value)
    return usd > 0 ? usd : null
}

async function submit(): Promise<void> {
    if (busy.value) return
    // A Catalog job with no task ticked already says so beside its tasks; nothing is sent. A held job installs none.
    if (catalogJob.value && !heldJob.value && keptTaskIds.value.length === 0) return
    const stop = problem()
    if (stop) {
        forgetRefusal()
        error.value = stop
        return
    }
    // #1796: with no AI account the teammate cannot be added, so the connect prompt opens instead of a preview of a
    // save that would be refused. Busy while the check reads, so a second press meanwhile does not open a second one.
    busy.value = true
    try {
        if (!(await requireAiAccount())) return
    } finally {
        busy.value = false
    }
    // A held job installs no Catalog task, so the preview names none as their work.
    const held = heldJob.value
    const kept = held ? [] : jobTasks.value.filter(task => keptTaskIds.value.includes(task.id))
    savePreview.value = buildTeammatePreview({
        name: name.value,
        jobTitle: held?.name.trim() || jobName.value,
        teamName: teamName(teamId.value),
        dailyCapUsd: dailyCapUsd(),
        taskNames: kept.map(task => taskDisplayName(task)),
    })
}

async function confirmPreview(): Promise<void> {
    savePreview.value = null
    await send()
}

async function send(): Promise<void> {
    if (busy.value) return
    // Busy before the AI-account check: that check may wait on a read, and a second send meanwhile must not get past.
    busy.value = true
    try {
        // #1796: a teammate runs on the AI account, so without one nothing is sent and the prompt opens.
        if (!(await requireAiAccount())) return
        forgetRefusal()
        const role = catalogJob.value
        const held = heldJob.value
        if (role && held) {
            // The company already holds this job on the chosen team: the teammate takes it, and no clone is sent.
            await addAsMember(held.id, null)
        } else if (role) {
            await addFromCatalog(role)
        } else {
            await addDescribed()
        }
    } finally {
        busy.value = false
    }
}

/**
 * Where a teammate made here lands: the Agents page to connect its own account, Create task for a described job's
 * first task, or its page. What comes after is kept for the tab first (`keepTeammateHandoff`): the teammate's page
 * lists the tools still to connect, and the Agents page offers the first task, so the own account never loses it.
 */
function destination(memberId: string, firstWork: HandedFirstTask | null = null): RouteLocationRaw {
    keepTeammateHandoff(memberId, {
        tools: needs.value.map(need => ({ tool: need.tool, tasks: need.tasks.map(task => taskDisplayName(task)) })),
        firstTask: firstWork,
    })
    if (aiProvider.value === 'own') return { path: '/agents', query: { connect: 'member', memberId } }
    return firstWork ? { path: '/tasks/new', query: { ...firstWork } } : `/members/${memberId}`
}

/** One step after the teammate exists, and how their page names it when it does not happen: "save their photo". */
type Step = { missed: string; cap?: boolean; run: () => Promise<unknown> }

/**
 * Everything after the member exists, one step at a time (#2128): a step that fails is noted and every other step is
 * still tried. The form is never sent again, which would only collide, as Create member does. With every step done
 * the teammate lands where it always has; otherwise on their page, which names once what did not happen and links to
 * any next step still waiting.
 */
async function finish(memberId: string, steps: Step[], firstWork: HandedFirstTask | null): Promise<void> {
    const missed: Step[] = []
    for (const step of steps) {
        try {
            await step.run()
        } catch {
            missed.push(step)
        }
    }
    const next = destination(memberId, firstWork)
    if (!missed.length) {
        await router.push(next)
        return
    }
    keepTeammateNotice(memberId, {
        name: name.value.trim(),
        missed: missed.map(step => step.missed),
        capMissed: missed.some(step => step.cap === true),
        connectOwn: aiProvider.value === 'own',
        firstTask: firstWork,
    })
    await router.push(`/members/${memberId}`)
}

/** The photo, extra teams and extra roles chosen under Advanced, with Create member's calls, as steps of their own. */
function extraSteps(memberId: string, jobRoleId: string | null): Step[] {
    const photo = avatar.value
    return [
        ...(photo ? [{ missed: 'save their photo', run: () => api.setMemberAvatar(memberId, photo) }] : []),
        ...extraTeamIds.value.map(id => ({
            missed: `add them to ${teamName(id) || 'another team'}`,
            run: () => api.addTeamMember(id, memberId, null),
        })),
        ...extraRoleIds.value
            .filter(id => id !== jobRoleId)
            .map(id => ({
                missed: `give them the ${roleName(id) || 'other'} job`,
                run: () => api.assignRole(memberId, id),
            })),
    ]
}

/**
 * A Catalog job: one role-clone call that makes the role, the teammate and the kept tasks on the team. When it is
 * refused as already cloned, the roles are read again (#2128): a copy the chosen team can hold turns the form to the
 * held job and nothing is made, so the person sees what they will get and confirms again; a copy only on another team
 * is named; anything else is refused. The teammate is never made while the screen shows tasks it did not install.
 */
async function addFromCatalog(role: CatalogRole): Promise<void> {
    let made: Awaited<ReturnType<typeof api.cloneCatalogRole>>
    try {
        const swaps = toolSwaps()
        const fills = slotFills()
        made = await api.cloneCatalogRole(role.id, {
            teamId: teamId.value,
            createTeammate: true,
            teammateName: name.value.trim(),
            taskIds: [...keptTaskIds.value],
            moneyCentsDay: moneyCents(),
            ...(swaps.length > 0 ? { toolSwaps: swaps } : {}),
            ...(fills.length > 0 ? { slotFills: fills } : {}),
        })
    } catch (caught) {
        if (refusalWords(caught).some(word => /already_cloned/.test(word))) {
            await heldAfterAll(role, caught)
        } else {
            refuse(caught)
        }
        return
    }
    // The teammate exists: the kept form is done with, whatever the steps after it do.
    clearTeammateDraft()
    const memberId = made.member?.id
    if (!memberId) {
        await router.push('/members')
        return
    }
    const text = persona.value.trim()
    const email = emailEdited.value ? emailTyped.value.trim() : ''
    const saved = [text ? 'personality' : '', email ? 'email' : ''].filter(Boolean).join(' and ')
    await finish(
        memberId,
        [
            ...(saved
                ? [
                      {
                          missed: `save their ${saved}`,
                          run: () =>
                              api.patchMember(memberId, {
                                  ...(text ? { persona: text } : {}),
                                  ...(email ? { email } : {}),
                              }),
                      },
                  ]
                : []),
            ...extraSteps(memberId, made.role?.id ?? null),
        ],
        null,
    )
}

/**
 * A clone refused as already cloned, though the loaded roles showed no copy of the job: the roles are read again, and
 * a copy found now turns the form to the held job. Otherwise the refusal is the tasks' slugs, and it is shown as sent.
 */
async function heldAfterAll(role: CatalogRole, caught: unknown): Promise<void> {
    const fresh = await api.roles().catch(() => null)
    if (fresh) roles.value = fresh
    if (heldCopyOf(roles.value, role)) {
        heldOnSend.value = true
        return
    }
    refuse(caught)
}

/**
 * A described job: the company's role of that name is reused, or one is created with the slug made from the name
 * (or the one typed under Advanced). Then the member is made and joins the team with it.
 */
async function addDescribed(): Promise<void> {
    const title = jobTitle.value.trim()
    // A role is one job across the company: one of that name is reused, and the teammate holds it on the chosen team.
    let roleId = roleNamed(roles.value, title)?.id
    if (!roleId) {
        try {
            const created = await api.createRole({ name: title, slug: slugValue.value.trim() || undefined })
            roles.value = [...roles.value, { id: created.id, name: created.name, slug: created.slug }]
            roleId = created.id
        } catch (caught) {
            refuse(caught)
            return
        }
    }
    await addAsMember(roleId, { goal: firstTask.value.trim(), role: roleId, team: teamId.value })
}

/**
 * Makes the member with Create member's calls, and has them join the chosen team with the job's role. The teammate
 * gets exactly the cap shown (#2128): an edited cap, the $10 shown when the company's Day $ could not be found, or the
 * nearest allowed cap shown for a Day $ out of bounds, is their own; the company's Day $ left as it is already holds
 * for them, so nothing is set.
 */
async function addAsMember(roleId: string, firstWork: HandedFirstTask | null): Promise<void> {
    let memberId: string
    try {
        const member = await api.createMember({
            name: name.value.trim(),
            email: emailEdited.value ? emailTyped.value.trim() || undefined : undefined,
            persona: persona.value.trim() || undefined,
        })
        memberId = member.id
    } catch (caught) {
        refuse(caught)
        return
    }
    // The teammate exists: the kept form is done with, whatever the steps after it do.
    clearTeammateDraft()
    const team = teamId.value
    const cents = moneyCents()
    const ownCap = moneyTyped.value || capSource.value !== 'company'
    await finish(
        memberId,
        [
            {
                missed: `add them to ${teamName(team) || 'their team'} as ${roleName(roleId) || jobName.value}`,
                run: () => api.addTeamMember(team, memberId, roleId),
            },
            ...extraSteps(memberId, roleId),
            ...(ownCap
                ? [
                      {
                          missed: `set their daily spend cap of $${(cents / 100).toFixed(2)}`,
                          cap: true,
                          run: () => api.patchSpendCap({ layer: 'member', layerId: memberId, moneyCentsDay: cents }),
                      },
                  ]
                : []),
        ],
        firstWork,
    )
}

/** The clone panel's refusal handling and words; the person's choices are kept for another try. */
function refuse(caught: unknown): void {
    const words = refusalWords(caught)
    const says = (pattern: RegExp) => words.some(word => pattern.test(word))
    if (isCraftedRefusal(caught)) {
        upgrade.value = 'crafted'
        error.value = CRAFTED_NOT_IN_PLAN_MESSAGE
    } else if (requestFailure(caught)?.status === 402 || says(/^plan_limit_reached$/)) {
        upgrade.value = 'members'
        error.value = 'Your plan has no room for another teammate.'
    } else if (says(/^member_email_taken$/)) {
        error.value = 'A teammate with this name already exists. Choose another name and retry.'
    } else if (says(/slug|already_cloned/)) {
        // Only a described job has a Short name under Advanced; a Catalog job's way out is another job.
        error.value = catalogJob.value
            ? 'Your company already has a job with this name. Choose another job.'
            : 'Your company already has a job with this name. Choose another job, or change its short name under Advanced.'
    } else if (says(/^missing_catalog_dependencies$/)) {
        error.value = 'Some kept tasks hand off to tasks you left out. Keep those tasks and try again.'
    } else if (says(/^invalid_tool_swap$/)) {
        error.value =
            'A tool you chose to use instead is no longer connected. Choose again, or keep the task and connect its tool later.'
    } else {
        error.value = 'Could not add your teammate. Your choices have been kept; try again.'
    }
}
</script>

<template>
	<section class="w-full space-y-6 p-8">
		<p class="text-muted-foreground text-sm">
			<RouterLink class="underline" to="/members">Members</RouterLink>
		</p>
		<!--
			#1756: the shared form shell (title, one-sentence purpose, fields, Advanced, actions bottom-right), one help
			line on every field, and a plain-words preview before the teammate is added.
		-->
		<ZhFormLayout
			title="Add a teammate"
			purpose="Choose the job, name your teammate and pick their team; their tasks come with the job."
			:advanced-count="job ? advancedFields.length : undefined"
			advanced-testid="advanced-trigger"
			data-testid="add-teammate-form"
			@submit.prevent="submit"
		>
			<div class="space-y-4">
				<ZhSectionHeading>The job</ZhSectionHeading>
				<ZhFormField v-slot="{ controlId, helpId }" label="Find a job in the Catalog" help="Search ready-made jobs by what the work is." example="customer support">
					<KitInput
						:id="controlId"
						v-model="jobSearch"
						type="search"
						placeholder="Search jobs"
						data-testid="job-search"
						:aria-describedby="helpId"
					/>
				</ZhFormField>
				<!-- #2128: a polite live region that is always there, so the loading line is announced as it comes and goes. -->
				<div aria-live="polite">
					<p v-if="jobsLoading" class="text-muted-foreground text-sm">Loading jobs…</p>
				</div>
				<p v-if="jobsFailed" role="alert" class="text-destructive text-sm">
					Could not load the Catalog's jobs. Search again, or describe the job.
				</p>
				<div v-if="jobs.length" class="flex flex-wrap gap-2" aria-label="Catalog jobs">
					<KitButton
						v-for="role in jobs"
						:key="role.id"
						type="button"
						size="sm"
						:variant="catalogJob?.id === role.id ? 'default' : 'outline'"
						:aria-pressed="catalogJob?.id === role.id"
						:title="roleLine(role)"
						:data-testid="`job-option-${role.id}`"
						@click="chooseJob(role)"
					>
						{{ role.name }}
					</KitButton>
				</div>
				<ZhEmptyState v-else-if="!jobsLoading && !jobsFailed" compact label="No jobs">
					{{ jobSearch.trim() ? 'Nothing in the Catalog matches that search.' : 'The Catalog has no jobs yet.' }}
					Describe the job instead.
				</ZhEmptyState>
				<KitButton
					type="button"
					size="sm"
					:variant="described ? 'default' : 'outline'"
					:aria-pressed="described"
					data-testid="describe-it"
					@click="describeIt"
				>
					Describe it
				</KitButton>
				<div v-if="described" class="grid gap-4 sm:grid-cols-2">
					<ZhFormField v-slot="{ controlId, helpId }" label="Job title" help="What the job is called, as your team would say it." example="Bookkeeper">
						<KitInput :id="controlId" v-model="jobTitle" maxlength="200" data-testid="job-title" :aria-describedby="helpId" />
					</ZhFormField>
					<ZhFormField v-slot="{ controlId, helpId }" label="What they do (optional)" help="One line on the work, shown with the job." example="Keeps the books and chases invoices">
						<KitInput :id="controlId" v-model="jobLine" maxlength="500" data-testid="job-line" :aria-describedby="helpId" />
					</ZhFormField>
				</div>
				<!-- #2128: the company already holds this job where the teammate joins, so it adds no tasks and offers none. -->
				<p v-if="catalogJob && heldJob" role="status" class="text-sm" data-testid="held-job-notice">
					{{ heldNotice }}
				</p>
				<template v-else-if="catalogJob">
					<p class="text-muted-foreground text-sm">{{ roleLine(catalogJob) }}</p>
					<ZhFormField label="Their tasks" help="The work they start with; untick any they should not do." example="Answer tickets" group>
					<fieldset class="space-y-2" :disabled="busy">
						<legend class="sr-only">Their tasks</legend>
						<div
							v-for="task in jobTasks"
							:key="task.id"
							class="flex items-start gap-2 text-sm"
							:data-testid="`job-task-${task.id}`"
						>
							<Checkbox
								:id="`add-teammate-task-${task.id}`"
								:model-value="keptTaskIds.includes(task.id)"
								:disabled="busy"
								@update:model-value="tickTask(task.id, $event)"
							/>
							<Label :for="`add-teammate-task-${task.id}`" class="grid gap-1">
								<span>{{ taskDisplayName(task) }}</span>
								<span class="text-muted-foreground font-normal">{{ task.currentVersion?.goal }}</span>
							</Label>
						</div>
					</fieldset>
					</ZhFormField>
					<p v-if="!keptTaskIds.length" role="alert" class="text-destructive text-sm">
						Choose at least one task so your teammate has work to do.
					</p>
					<p v-else class="text-muted-foreground text-sm">
						The ticked tasks are their first work. No run or schedule starts on its own.
					</p>
				</template>
			</div>

			<template v-if="job">
				<div class="space-y-4">
					<ZhSectionHeading>Your teammate</ZhSectionHeading>
					<ZhFormField v-slot="{ controlId, helpId }" label="Name" help="What your team will call them." example="Sam">
						<KitInput
							:id="controlId"
							:model-value="name"
							:maxlength="NAME_MAX"
							data-testid="teammate-name"
							:aria-describedby="helpId"
							@update:model-value="typeName"
						/>
					</ZhFormField>
					<ZhFormField v-slot="{ controlId, helpId }" label="Personality" help="How they work and sound, in a sentence or two." example="Warm, quick and exact">
						<KitTextarea
							:id="controlId"
							:model-value="persona"
							rows="3"
							maxlength="500"
							data-testid="teammate-persona"
							:aria-describedby="helpId"
							@update:model-value="typePersona"
						/>
					</ZhFormField>
					<ZhFormField
						v-slot="{ controlId, helpId }"
						label="Team"
						label-id="add-teammate-team"
						help="The team they join and work for."
						example="Support Desk"
						data-testid="team-picker"
					>
						<KitSelect
							v-if="teams.length"
							:model-value="teamId"
							:disabled="busy"
							aria-label="Team"
							@update:model-value="chooseTeam"
						>
							<SelectTrigger :id="controlId" class="w-full" aria-labelledby="add-teammate-team" :aria-describedby="helpId">
								<SelectValue placeholder="Choose a team" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem v-for="team in teams" :key="team.id" :value="team.id">{{ team.name }}</SelectItem>
							</SelectContent>
						</KitSelect>
						<p v-else class="text-sm">
							Your company has no team yet.
							<RouterLink class="underline" to="/teams/new" @click="keepDraft()">Create a team first</RouterLink>
						</p>
					</ZhFormField>
				</div>

				<ZhFormField
					label="AI account"
					help="Whose AI account their runs use and are billed to."
					example="your company's account"
					group
					data-testid="ai-provider"
				>
					<RadioGroup
						:model-value="aiProvider"
						:disabled="busy"
						aria-label="AI account"
						@update:model-value="chooseProvider"
					>
						<RadioGroupItem value="shared">Use your company’s AI account</RadioGroupItem>
						<RadioGroupItem value="own">Give this teammate its own account</RadioGroupItem>
					</RadioGroup>
					<p class="text-muted-foreground text-sm">
						{{
							aiProvider === 'own'
								? 'Once they are added, you go to the Agents page to connect their own account there.'
								: 'They run on the account your company has connected, like the rest of your team.'
						}}
					</p>
				</ZhFormField>

				<ZhFormField
					v-if="catalogJob && !heldJob"
					label="Tools"
					help="The tools their tasks use; connect each now or keep it for later."
					example="Slack, to connect later"
					group
					data-testid="tool-needs"
				>
					<div class="space-y-3">
					<p v-if="reviewState === 'loading'" class="text-muted-foreground text-sm">
						Checking which tools this job needs…
					</p>
					<p v-else-if="reviewState === 'failed'" class="text-muted-foreground text-sm">
						The tool check did not answer. Add your teammate, then connect their tools on the Tools page.
					</p>
					<ZhEmptyState v-else-if="!needs.length" compact label="Nothing to connect" data-testid="tools-none">
						Your company has connected every tool these tasks use.
					</ZhEmptyState>
					<div
						v-for="need in needs"
						:key="need.tool"
						class="space-y-2"
						:data-testid="`tool-row-${need.tool}`"
					>
						<div class="flex flex-wrap items-center justify-between gap-2">
							<p class="text-sm font-medium">{{ jobName }} needs {{ toolLabel(need.tool, mode) }}</p>
							<KitButton type="button" size="sm" variant="outline" @click="connect(need.tool)">
								Connect
							</KitButton>
						</div>
						<ul class="text-muted-foreground list-disc space-y-1 pl-5 text-sm">
							<li v-for="task in need.tasks" :key="task.id">
								<span class="text-foreground">{{ taskDisplayName(task) }}</span>:
								{{ task.currentVersion?.goal }}
							</li>
						</ul>
						<div
							v-for="task in swappable(need)"
							:key="`swap-${task.id}`"
							class="grid gap-1 text-sm"
							:data-testid="`tool-swap-${task.id}-${need.tool}`"
						>
							<span :id="`add-teammate-swap-${task.id}-${need.tool}`">
								For {{ taskDisplayName(task) }}
							</span>
							<KitSelect
								:model-value="choiceOf(task.id, need.tool)"
								:disabled="busy"
								@update:model-value="setChoice(task, need.tool, $event)"
							>
								<SelectTrigger :aria-labelledby="`add-teammate-swap-${task.id}-${need.tool}`">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem :value="KEEP">Keep, and connect {{ toolLabel(need.tool, mode) }} later</SelectItem>
									<SelectItem
										v-for="candidate in candidatesOf(task.id)"
										:key="candidate"
										:value="`${USE}${candidate}`"
									>
										Use {{ toolLabel(candidate, mode) }} instead
									</SelectItem>
									<SelectItem :value="DROP">Drop this task</SelectItem>
								</SelectContent>
							</KitSelect>
						</div>
					</div>
					</div>
				</ZhFormField>

				<!-- Tool slots: the tasks name some tools by a slot, which the company fills with a connection of its own. -->
				<ZhFormField
					v-if="catalogJob && slotRows.length"
					label="Slots to fill"
					help="Which of your connections each slot uses; change it later on the Tools page."
					example="Code host"
					group
					data-testid="slot-fills"
				>
					<div class="space-y-3">
						<div
							v-for="slot in slotRows"
							:key="slot.key"
							class="grid gap-1 text-sm"
							:data-testid="`slot-row-${slot.key}`"
						>
							<span :id="`add-teammate-slot-${slot.key}`" class="font-medium">{{ slot.label }}</span>
							<p v-if="slot.description" class="text-muted-foreground">{{ slot.description }}</p>
							<p class="text-muted-foreground">
								Needed by {{ slot.tasks.map(task => taskDisplayName(task)).join(', ') }}
							</p>
							<KitSelect
								v-if="slotOptions(slot).length"
								:model-value="slotChoice(slot)"
								:disabled="busy"
								@update:model-value="chooseSlot(slot, $event)"
							>
								<SelectTrigger :aria-labelledby="`add-teammate-slot-${slot.key}`">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem v-if="!slot.filledBy" :value="NO_FILL">Not now, fill it later</SelectItem>
									<SelectItem v-for="option in slotOptions(slot)" :key="option.bindingId" :value="option.bindingId">
										{{ option.name }}
									</SelectItem>
								</SelectContent>
							</KitSelect>
							<p v-else class="text-muted-foreground">
								No connection can fill this yet. Connect a tool on the
								<RouterLink to="/tools">Tools page</RouterLink> first.
							</p>
						</div>
					</div>
				</ZhFormField>

				<ZhFormField
					v-if="described"
					v-slot="{ controlId, helpId }"
					label="What should they do first? One sentence."
					help="You finish it on Create task; nothing runs until you start it."
					example="Reconcile last month's invoices"
				>
					<KitInput :id="controlId" v-model="firstTask" maxlength="500" data-testid="first-task" :aria-describedby="helpId" />
				</ZhFormField>

				<div class="space-y-2" data-testid="monthly-cost-estimate">
					<ZhSectionHeading>Expected monthly cost</ZhSectionHeading>
					<div class="space-y-2" aria-live="polite">
						<p v-if="connections === undefined" class="text-muted-foreground text-sm">Working out the monthly cost…</p>
						<p v-for="line in monthlyCost" :key="line" class="text-sm">{{ line }}</p>
					</div>
					<p class="text-muted-foreground text-sm">AI account cost only. Your ZeroHuman plan is billed separately.</p>
				</div>
			</template>

			<template v-if="job" #advanced>
							<ZhFormField v-slot="{ controlId, helpId }" label="Email" help="Their work address, made from the name if you leave it." example="sam@acme.zerohuman.com">
								<KitInput
									:id="controlId"
									:aria-describedby="helpId"
									:model-value="emailValue"
									type="text"
									inputmode="email"
									autocomplete="off"
									spellcheck="false"
									placeholder="from the name if empty"
									data-testid="member-email"
									@update:model-value="typeEmail"
								/>
							</ZhFormField>
							<ZhFormField label="Photo" label-id="add-teammate-photo" help="Optional; their initials stand in until you pick one." example="a square headshot" group>
								<div class="flex items-center gap-3">
									<MemberAvatar :member="preview" class="size-12" />
									<KitButton
										type="button"
										size="sm"
										variant="outline"
										aria-describedby="add-teammate-photo"
										data-testid="pick-avatar"
										:disabled="busy"
										@click="pickAvatar"
									>
										{{ avatar ? 'Change photo' : 'Upload photo' }}
									</KitButton>
									<!-- A picked photo is only a choice until the teammate is added, so it can be taken back to initials. -->
									<KitButton
										v-if="avatar"
										type="button"
										size="sm"
										variant="ghost"
										data-testid="remove-avatar"
										:disabled="busy"
										@click="removeAvatar"
									>
										Remove photo
									</KitButton>
									<KitInput
										ref="avatarInput"
										type="file"
										accept="image/png,image/jpeg,image/webp,image/gif"
										class="hidden"
										data-testid="avatar-file"
										@change="onAvatarPicked"
									/>
								</div>
							</ZhFormField>
							<ZhFormField
								label="More teams"
								label-id="add-teammate-extra-teams"
								help="Other teams they also work for, beyond the one above."
								example="Finance"
								group
								data-testid="extra-teams"
							>
								<TagsInput
									:model-value="extraTeamIds"
									:display-value="teamName"
									:disabled="busy"
									class="min-h-9"
									@remove-tag="removeExtraTeam"
								>
									<TagsInputItem v-for="id in extraTeamIds" :key="id" :value="id">
										<TagsInputItemText />
										<TagsInputItemDelete tabindex="0" :aria-label="`Remove from ${teamName(id)}`" />
									</TagsInputItem>
									<span v-if="!extraTeamIds.length" class="text-muted-foreground px-1 text-sm">
										Only the team above.
									</span>
								</TagsInput>
								<KitSelect
									v-if="extraTeamChoices.length"
									:model-value="NOTHING"
									:disabled="busy"
									aria-label="Add to another team"
									@update:model-value="addExtraTeam"
								>
									<SelectTrigger size="sm" aria-labelledby="add-teammate-extra-teams">
										<SelectValue placeholder="Add to another team…" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem v-for="team in extraTeamChoices" :key="team.id" :value="team.id">
											{{ team.name }}
										</SelectItem>
									</SelectContent>
								</KitSelect>
							</ZhFormField>
							<ZhFormField
								label="More jobs"
								label-id="add-teammate-extra-roles"
								help="Other jobs they also hold, beyond the one the job gives."
								example="Account Executive"
								group
								data-testid="extra-roles"
							>
								<TagsInput
									:model-value="extraRoleIds"
									:display-value="roleName"
									:disabled="busy"
									class="min-h-9"
									@remove-tag="removeExtraRole"
								>
									<TagsInputItem v-for="id in extraRoleIds" :key="id" :value="id">
										<TagsInputItemText />
										<TagsInputItemDelete tabindex="0" :aria-label="`Remove ${roleName(id)}`" />
									</TagsInputItem>
									<span v-if="!extraRoleIds.length" class="text-muted-foreground px-1 text-sm">
										Only the job above.
									</span>
								</TagsInput>
								<KitSelect
									v-if="extraRoleChoices.length"
									:model-value="NOTHING"
									:disabled="busy"
									aria-label="Add another job"
									@update:model-value="addExtraRole"
								>
									<SelectTrigger size="sm" aria-labelledby="add-teammate-extra-roles">
										<SelectValue placeholder="Add another job…" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem v-for="role in extraRoleChoices" :key="role.id" :value="role.id">
											{{ role.name }}
										</SelectItem>
									</SelectContent>
								</KitSelect>
							</ZhFormField>
							<ZhFormField
								v-slot="{ controlId, helpId }"
								label="Daily spend cap (USD)"
								:help="capHelp"
								example="25.00"
							>
								<KitInput
									:id="controlId"
									:model-value="money"
									type="number"
									min="0.01"
									max="10000"
									step="0.01"
									data-testid="daily-cap"
									:aria-describedby="helpId"
									@update:model-value="typeMoney"
								/>
							</ZhFormField>
							<ZhFormField
								v-if="described"
								v-slot="{ controlId, helpId }"
								label="Short name"
								help="Made from the job title; used only for a new job."
								example="bookkeeper"
							>
								<KitInput
									:id="controlId"
									:model-value="slugValue"
									maxlength="64"
									data-testid="role-slug"
									:aria-describedby="helpId"
									@update:model-value="typeSlug"
								/>
							</ZhFormField>
			</template>

			<template #status>
				<p v-if="heldOnSend" role="status" class="text-muted-foreground text-sm">
					Nothing was added: your company already has this job. Check the form above, then add your teammate again.
				</p>
				<p v-if="error" role="alert" class="text-destructive text-sm">{{ error }}</p>
				<RouterLink v-if="upgrade === 'members'" class="text-sm underline" to="/billing#plans">Upgrade to add</RouterLink>
				<RouterLink v-else-if="upgrade === 'crafted'" class="text-sm underline" to="/billing#plans">
					See plans on Billing
				</RouterLink>
			</template>
			<!-- Cancel is there before a job is chosen too, so the way out is never only the breadcrumb (#2128). -->
			<template #cancel>
					<KitButton type="button" variant="outline" :disabled="busy" @click="cancel">
						Cancel
					</KitButton>
			</template>
			<template v-if="job" #primary>
					<KitButton type="submit" :disabled="busy" data-testid="add-teammate-submit">
						{{ busy ? 'Adding…' : 'Add teammate' }}
					</KitButton>
			</template>
			<template #preview>
				<ZhSavePreview v-if="savePreview" :lines="savePreview" confirm-label="Add teammate" :pending="busy" @confirm="confirmPreview" @edit="savePreview = null" />
			</template>
		</ZhFormLayout>
	</section>
</template>
